import {Box, IconButton, useColorModeValue} from '@chakra-ui/react';
import {motion} from 'framer-motion';
import {TablerStar, TablerStarFilled} from '../../icons';

const MotionBox = motion(Box);

interface FavoriteStarProps {
  isFavorite: boolean;
  onToggle: () => void;
  language: string;
  size?: number;
}

// The star affordance shown in every language row. Filled amber when favorited,
// quiet outline otherwise. It stops click/keydown propagation so starring never
// also selects the row, and gives a small pop when toggled on for tactile
// feedback that pairs with the row sliding up into the favorites group.
export default function FavoriteStar({
  isFavorite,
  onToggle,
  language,
  size = 18,
}: FavoriteStarProps) {
  const favoriteColor = useColorModeValue('yellow.400', 'yellow.300');
  const idleColor = useColorModeValue('gray.400', 'gray.500');
  const hoverBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  const handleToggle = (event: {stopPropagation: () => void}) => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <IconButton
      as="span"
      role="button"
      tabIndex={0}
      aria-label={
        isFavorite ? `Unfavorite ${language}` : `Favorite ${language}`
      }
      aria-pressed={isFavorite}
      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      icon={
        <MotionBox
          as="span"
          display="inline-flex"
          // Re-key on the favorited state so the icon swap replays the pop.
          key={isFavorite ? 'on' : 'off'}
          initial={isFavorite ? {scale: 0.5} : false}
          animate={isFavorite ? {scale: [0.5, 1.3, 1]} : {scale: 1}}
          transition={{duration: 0.28, ease: [0.22, 1, 0.36, 1]}}
        >
          {isFavorite ? (
            <TablerStarFilled size={size} />
          ) : (
            <TablerStar size={size} />
          )}
        </MotionBox>
      }
      size="sm"
      variant="ghost"
      minW="32px"
      h="32px"
      borderRadius="full"
      flexShrink={0}
      color={isFavorite ? favoriteColor : idleColor}
      // Keep focus on the surrounding input so the typeahead dropdown's onBlur
      // doesn't close before the toggle registers (mirrors the option buttons).
      onMouseDown={event => event.preventDefault()}
      onClick={handleToggle}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleToggle(event);
        }
      }}
      _hover={{bg: hoverBg, color: isFavorite ? favoriteColor : idleColor}}
    />
  );
}
